import { TeamOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Divider,
  Empty,
  InputNumber,
  Skeleton,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useSearchAvailableRooms,
  useGetHotelStoreDetails,
} from "../../../api/api.get";
import { OrgDataComponent } from "../../../components/org.data.component";
import RoomImagesBottomSheet from "../../../components/room.images.botomsheet";
import { IAvailableRoom, IAvailableRoomsSearch } from "../../../types/types";
import { capitalize } from "../../../utils/functions";
import { toFixedDisplay } from "../../../utils/number.utils";

const DATE_FORMAT = "YYYY-MM-DD";

export default function HotelReservationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: storeDetailsData, error } = useGetHotelStoreDetails(id);

  // Currency is derived from the property's country (₹ for India, else $).
  const currency = storeDetailsData?.country === "IND" ? "₹" : "$";

  // Default to tonight → tomorrow so the listing has something to show.
  const [checkInDate, setCheckInDate] = useState<Dayjs | null>(dayjs());
  const [checkOutDate, setCheckOutDate] = useState<Dayjs | null>(
    dayjs().add(1, "day"),
  );
  const [numberOfGuests, setNumberOfGuests] = useState<number | null>(2);

  // The criteria actually sent to the API — only updated when the user searches,
  // so typing in the form doesn't refetch on every keystroke.
  const [appliedSearch, setAppliedSearch] = useState<IAvailableRoomsSearch>();

  const { data: rooms, isFetching } = useSearchAvailableRooms(
    id,
    appliedSearch,
  );

  // Room whose photos are shown in the bottom sheet (undefined = closed).
  const [selectedRoom, setSelectedRoom] = useState<IAvailableRoom>();

  useEffect(() => {
    const _error: any = error;
    if (_error?.status === 404) {
      navigate("/not-found");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Run an initial search on mount with the default dates.
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datesValid =
    !!checkInDate && !!checkOutDate && checkOutDate.isAfter(checkInDate, "day");

  const handleSearch = () => {
    if (!checkInDate || !checkOutDate) {
      return;
    }
    setAppliedSearch({
      checkInDate: checkInDate.format(DATE_FORMAT),
      checkOutDate: checkOutDate.format(DATE_FORMAT),
      numberOfGuests: numberOfGuests ?? undefined,
    });
  };

  const nights =
    checkInDate && checkOutDate
      ? Math.max(checkOutDate.diff(checkInDate, "day"), 0)
      : 0;

  return (
    <div
      className="mx-auto max-w-5xl min-h-screen"
      style={{
        background: storeDetailsData?.bodyBackgroundColor ?? "white",
        color: storeDetailsData?.bodyFontColor ?? "black",
        paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
        borderRight: "1px solid darkgrey",
        borderLeft: "1px solid darkgrey",
      }}
    >
      <div
        className="pt-8 px-4"
        style={{
          background: storeDetailsData?.headerBackgroundColor ?? "white",
          color: storeDetailsData?.headerFontColor ?? "black",
          borderBottom: "1px solid darkgrey",
        }}
      >
        <OrgDataComponent storeDetailsData={storeDetailsData} variant="bold" />
        <Divider />
      </div>

      <div className="px-4">
        <div className="mt-6 mb-5">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-500">
            ✦ Book your stay
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            Find Your{" "}
            <span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 bg-clip-text text-transparent">
              Perfect Stay
            </span>
          </h1>
          <p className="mt-2 text-sm sm:text-base opacity-80">
            Pick your dates and guests — we'll show you rooms ready to book.
          </p>
        </div>

        {/* Search criteria */}
        <div className="rounded-xl border border-gray-200 bg-white/70 p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">
                Check-in
              </label>
              <DatePicker
                value={checkInDate}
                format={DATE_FORMAT}
                allowClear={false}
                disabledDate={(d) => d && d < dayjs().startOf("day")}
                onChange={(d) => {
                  setCheckInDate(d);
                  // Keep check-out strictly after check-in.
                  if (d && checkOutDate && !checkOutDate.isAfter(d, "day")) {
                    setCheckOutDate(d.add(1, "day"));
                  }
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">
                Check-out
              </label>
              <DatePicker
                value={checkOutDate}
                format={DATE_FORMAT}
                allowClear={false}
                disabledDate={(d) =>
                  d && checkInDate
                    ? d <= checkInDate.startOf("day")
                    : d && d < dayjs().startOf("day")
                }
                onChange={(d) => setCheckOutDate(d)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">
                Guests
              </label>
              <InputNumber
                min={1}
                max={20}
                value={numberOfGuests}
                onChange={(v) => setNumberOfGuests(v)}
                prefix={<TeamOutlined />}
                style={{ width: 120 }}
              />
            </div>

            <Button
              type="primary"
              size="large"
              onClick={handleSearch}
              disabled={!datesValid}
            >
              Search
            </Button>
          </div>
          {!datesValid && (
            <p className="text-xs text-red-500 mt-2">
              Check-out must be after check-in.
            </p>
          )}
        </div>

        {/* Results */}
        <Skeleton active loading={isFetching}>
          {(!rooms || rooms.length === 0) && (
            <Empty
              description="No rooms available for these dates. Try different dates or fewer guests."
              className="my-16"
            />
          )}

          {rooms && rooms.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rooms.map((room) => (
                <RoomCard
                  key={room.id}
                  room={room}
                  currency={currency}
                  nights={nights}
                  onClick={() => setSelectedRoom(room)}
                />
              ))}
            </div>
          )}
        </Skeleton>

        <RoomImagesBottomSheet
          open={!!selectedRoom}
          room={selectedRoom}
          nights={nights}
          currency={currency}
          onClose={() => setSelectedRoom(undefined)}
        />
      </div>
    </div>
  );
}

function RoomCard({
  room,
  currency,
  nights,
  onClick,
}: {
  room: IAvailableRoom;
  currency?: string;
  nights: number;
  onClick: () => void;
}) {
  const cover = room.images?.[0] ?? "/assets/images/no-picture.svg";
  const total = nights > 0 ? room.pricePerNight * nights : room.pricePerNight;
  const imageCount = room.images?.length ?? 0;

  return (
    <div
      role="button"
      onClick={onClick}
      className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm flex flex-col cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="h-44 w-full bg-gray-100 relative">
        <img
          src={cover}
          alt={room.name ?? "room"}
          className="h-full w-full object-cover"
        />
        {imageCount > 1 && (
          <div className="absolute bottom-2 right-2 rounded-full bg-black/50 text-white text-xs px-2 py-0.5">
            {imageCount} photos
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1 text-gray-800">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-bold leading-tight">
              {capitalize(room.name) || "Room"}
            </h3>
            {room.roomTypeName && (
              <span className="text-xs text-gray-500">
                {capitalize(room.roomTypeName)}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
            <TeamOutlined /> {room.capacity}
          </span>
        </div>

        {room.description && (
          <p className="text-sm text-gray-600 line-clamp-3">
            {room.description}
          </p>
        )}

        <div className="mt-auto pt-2 flex items-end justify-between">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-gray-900">
              {currency}
              {toFixedDisplay(room.pricePerNight)}
              <span className="text-xs font-normal text-gray-500">
                {" "}
                / night
              </span>
            </span>
            {nights > 0 && (
              <span className="text-xs text-gray-500">
                {currency}
                {toFixedDisplay(total)} for {nights} night
                {nights > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <Button type="primary" disabled>
            Reserve
          </Button>
        </div>
      </div>
    </div>
  );
}
